import { Helmet } from "react-helmet-async";


/**
 * SEO helper for PUBLIC marketing/info pages (Landing, FAQ, Über uns,
 * Kontakt, Impressum, Datenschutz, Nutzungsbedingungen).
 *
 * Renders a unique <title>, <meta name="description">, canonical link,
 * hreflang alternates (de / en / x-default), Open Graph + Twitter
 * mirrors, and any extra JSON-LD blocks passed via `jsonLd`.
 *
 * Important:
 *  - This component must NOT be used on authenticated/internal pages —
 *    those use <NoIndex /> instead.
 *  - The canonical/og:url always self-reference the current locale.
 *  - `slug` is the path AFTER the locale segment, without leading slash.
 *    Use "" for the landing page itself.
 */
export interface PublicSeoProps {
  /** Path after the locale segment, no leading slash. "" = landing. */
  slug: string;
  titleDe: string;
  titleEn: string;
  descriptionDe: string;
  descriptionEn: string;
  /** Optional additional JSON-LD schema objects (already plain objects). */
  jsonLd?: Array<Record<string, unknown>>;
  /** Override the absolute OG image URL. Falls back to the sitewide one. */
  ogImage?: string;
}

const BASE = "https://aimupdarts.com";

export const PublicSeo = ({
  slug,
  titleDe,
  descriptionDe,
  jsonLd,
  ogImage,
}: PublicSeoProps) => {
  // Die öffentliche Website ist derzeit rein deutschsprachig (DACH).
  // Es werden bewusst keine englischen hreflang-Signale ausgegeben.
  const title = titleDe;
  const description = descriptionDe;

  const path = slug ? `/${slug}` : "";
  const canonical = `${BASE}/de${path}`;

  return (
    <Helmet>
      <html lang="de" />
      <title>{title}</title>
      <meta name="description" content={description} />

      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="de" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="de_DE" />
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}


      {jsonLd?.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
};

export default PublicSeo;
